/* eslint-disable camelcase */
import { FC } from 'react';
import { isValidImageUrl } from '../../../bookings/util/isValidImageUrl';
import { StyledLink } from '../../../common/components/Link.styles';
import { Heading } from '../../../common/components/Typography/Heading/Heading';
import { Api3Favourite } from '../../../common/schema/api3/types/Api3Favourite';
import { FontWeight } from '../../../common/styles/constants';
import {
  MyCareTeamCard,
  MyCareTeamCardAction,
  MyCareTeamCardAddress,
  MyCareTeamIcon,
  MyCareTeamIconWrapper,
} from './MyCareTeamSection.styles';
import { MyCareTeamPractice, MyCareTeamPractitioner } from './types';
import { generateMyCareTeamPracticeBookUrl, generateMyCareTeamPractitionerBookUrl } from './url';

export const MyCareTeamAvatar: FC<{ avatar?: string | null }> = ({ avatar }) => (
  <MyCareTeamIconWrapper>
    <MyCareTeamIcon
      src={isValidImageUrl(avatar) ? String(avatar) : '/public/images/GP_20210817091601.svg'}
      htmlAttributes={{ alt: 'My Care Team Avatar' }}
      imgixParams={{ fit: 'crop' }}
      height={60}
      width={60}
      isAvatar={isValidImageUrl(avatar)}
    />
  </MyCareTeamIconWrapper>
);

export type MyCareTeamCardProps = Omit<Api3Favourite, 'practice' | 'practitioner'> & {
  openAppointmentsModal: (appointmentUrl: string) => void;
};

export type MyCareTeamPracticeCardProps = MyCareTeamPractice & MyCareTeamCardProps;

export const MyCareTeamPracticeCard: FC<MyCareTeamPracticeCardProps> = props => {
  const { practice, openAppointmentsModal } = props;
  const bookUrl = generateMyCareTeamPracticeBookUrl(props);
  return (
    <MyCareTeamCard
      imagePosition="left"
      imageElement={<MyCareTeamAvatar avatar={practice?.avatar} />}
      compact
    >
      <StyledLink to={practice.seo_url ?? ''}>
        <Heading h4 weight={FontWeight.SEMI_BOLD}>
          {practice.name}
        </Heading>
      </StyledLink>

      {practice.address && (
        <MyCareTeamCardAddress>
          {practice.address.street}, {practice.address.suburb}, {practice.address.state},{' '}
          {practice.address.postcode}
        </MyCareTeamCardAddress>
      )}

      {bookUrl && (
        <MyCareTeamCardAction
          onClick={() => openAppointmentsModal(bookUrl)}
          options={{ keyline: true, small: true }}
        >
          Book
        </MyCareTeamCardAction>
      )}
    </MyCareTeamCard>
  );
};

type MyCareTeamPractitionerCardProps = MyCareTeamPractitioner & MyCareTeamCardProps;

export const MyCareTeamPractitionerCard: FC<MyCareTeamPractitionerCardProps> = props => {
  const { practitioner, openAppointmentsModal } = props;
  const bookUrl = generateMyCareTeamPractitionerBookUrl(props);

  return (
    <MyCareTeamCard
      imagePosition="left"
      imageElement={<MyCareTeamAvatar avatar={practitioner?.avatar} />}
      compact
    >
      <StyledLink to={practitioner.seo_url ?? ''}>
        <Heading h4 weight={FontWeight.SEMI_BOLD}>
          {practitioner?.name}
        </Heading>
      </StyledLink>
      {/* Practitioners can have multiple addresses and we don't have a design to support that yet 
    <MyCareTeamCardAddress>
      {practitioner?.practices[0]?.address.street}, {practitioner?.practices[0]?.address.suburb},{' '}
      {practitioner?.practices[0]?.address.state}, {practitioner?.practices[0]?.address.postcode}
    </MyCareTeamCardAddress> */}
      {bookUrl && (
        <MyCareTeamCardAction
          onClick={() => openAppointmentsModal(bookUrl)}
          options={{ keyline: true, small: true }}
        >
          Book
        </MyCareTeamCardAction>
      )}
    </MyCareTeamCard>
  );
};
